/**
 * React Query hook wrappers for back-office endpoints.
 *
 * Mirrors the prototype Orval-generated hooks (useGetEcoleDashboard, …) so
 * pages can be ported nearly verbatim. Each "list" hook returns `{ data,
 * isLoading, error }`; each mutation returns `{ mutate, isPending }`.
 */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { toast } from "sonner";
import * as api from "./api";
import { friendlyError } from "./errors";
import { useAnneeScope } from "./annee-scope";

// ---------- Parent self-service (/api/me/*) ----------

export const getMyElevesQueryKey = () => ["me", "eleves"] as const;
export function useMyEleves() {
  return useQuery({ queryKey: getMyElevesQueryKey(), queryFn: api.listMyEleves });
}

export const getEleveSoldesQueryKey = (id: string) =>
  ["me", "eleves", id, "soldes"] as const;
/**
 * Soldes (totaux + par motif) d'un élève du parent connecté. `enabled` permet
 * de ne charger qu'à l'ouverture d'une modale ou quand un élève est sélectionné.
 */
export function useEleveSoldes(id: string, opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: getEleveSoldesQueryKey(id),
    queryFn: () => api.getEleveSoldes(id),
    enabled: (opts?.enabled ?? true) && !!id,
  });
}

export const getMyDemandesQueryKey = () => ["me", "demandes"] as const;
/** Demandes de rattachement du parent connecté (suivi de statut). */
export function useMyDemandes() {
  return useQuery({ queryKey: getMyDemandesQueryKey(), queryFn: api.getMyDemandes });
}

export const getMyPaiementsQueryKey = (params?: {
  status?: string;
  page?: number;
  size?: number;
}) => ["me", "paiements", params ?? {}] as const;
export function useMyPaiements(params?: { status?: string; page?: number; size?: number }) {
  return useQuery({
    queryKey: getMyPaiementsQueryKey(params),
    queryFn: () => api.listMyPaiements(params),
  });
}

/**
 * Query key for the public payment status (GET /api/paiements-public/{token}).
 * Page-level code should reuse this key for its `getPaiementByToken` query so
 * that {@link useClaimPaiement} can invalidate it on a successful claim.
 */
export const getPaiementPublicQueryKey = (token: string) =>
  ["paiement-public", token] as const;

export function useClaimPaiement(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: api.ClaimRequest }) => api.claimPaiement(token, data),
    onSuccess: (res: api.ClaimResponse) => {
      toast.success(res.message ?? "Compte créé avec succès");
      qc.invalidateQueries({ queryKey: getPaiementPublicQueryKey(token) });
    },
    onError: (err: unknown) => {
      toast.error(friendlyError(err));
    },
  });
}

/**
 * Détache un élève du tableau de bord parent (DELETE /api/me/eleves/{id}).
 * N'enlève PAS l'élève de l'école — seulement le rattachement parent.
 * → invalide la liste des enfants + toast de confirmation.
 */
export function useDetachEleve() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.detachEleve(id),
    onSuccess: () => {
      toast.success("Enfant retiré de votre espace.");
      qc.invalidateQueries({ queryKey: getMyElevesQueryKey() });
    },
    onError: (err: unknown) => toast.error(friendlyError(err)),
  });
}

/** Met à jour le lien de parenté d'un élève → invalide la liste des enfants. */
export function useUpdateEleveLien() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, lien }: { id: string; lien: api.LienParente }) =>
      api.updateEleveLien(id, lien),
    onSuccess: () => {
      toast.success("Lien de parenté mis à jour.");
      qc.invalidateQueries({ queryKey: getMyElevesQueryKey() });
    },
    onError: (err: unknown) => toast.error(friendlyError(err)),
  });
}

/** Profil contact du parent connecté (GET /api/me/profil). */
export const getProfilQueryKey = () => ["me", "profil"] as const;
export function useProfil(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: getProfilQueryKey(),
    queryFn: api.getProfil,
    enabled: opts?.enabled ?? true,
  });
}

/** Met à jour le profil contact → invalide la query profil + toast. */
export function useUpdateProfil() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: api.ProfilUpdate) => api.updateProfil(body),
    onSuccess: () => {
      toast.success("Profil mis à jour.");
      qc.invalidateQueries({ queryKey: getProfilQueryKey() });
    },
    onError: (err: unknown) => toast.error(friendlyError(err)),
  });
}

// ---------- Rattachement parent↔élève ----------

/** Demandes de rattachement de l'école (back-office économe), par statut. */
export function useEcoleDemandes(statut: api.EcoleDemandeStatut = "PENDING") {
  return useQuery({
    queryKey: ["ecole", "demandes", statut],
    queryFn: () => api.listEcoleDemandes(statut),
  });
}

/** Traite une demande (lier/créer/fusionner/rejeter) → invalide demandes + roster élèves. */
export function useTraiterDemande() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: api.TraiterInput }) => api.traiterDemande(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ecole", "demandes"] });
      qc.invalidateQueries({ queryKey: getListEcoleElevesQueryKey() });
    },
  });
}

/** Génère un code de rattachement pour un élève (économe). */
export function useGenererCode() {
  return useMutation({ mutationFn: (eleveId: string) => api.genererCodeRattachement(eleveId) });
}

/** Doublons suspects (même identité normalisée dans l'école). */
export function useDoublons() {
  return useQuery({ queryKey: ["ecole", "doublons"], queryFn: () => api.listDoublons() });
}

/** Parent : revendique un élève via le code de rattachement (Voie B) → invalide ses enfants. */
export function useClaimByCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => api.claimEleveByCode(code),
    onSuccess: () => qc.invalidateQueries({ queryKey: getMyElevesQueryKey() }),
  });
}

/** Parent : dépose une demande de rattachement (Voie C). */
export function useCreateDemande() {
  return useMutation({ mutationFn: (body: api.DemandeInput) => api.createDemande(body) });
}

/**
 * Parent authentifié : rattache un élève déjà payé via le token de paiement
 * (Voie A) → invalide ses enfants. Endpoint authentifié, pas le claim guest.
 */
export function useClaimElevePaiement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => api.claimElevePaiement(token),
    onSuccess: () => qc.invalidateQueries({ queryKey: getMyElevesQueryKey() }),
  });
}

// ---------- École ----------

// NOTE Lot 1 — Les clés back-office école incluent l'`anneeId` (année scolaire
// de travail) pour que le changement d'année déclenche un refetch (chaque année
// a son propre cache). Les helpers `getList*QueryKey()` gardent le PRÉFIXE à 2
// segments (`["ecole","eleves"]`) pour l'invalidation : un `invalidateQueries`
// sur ce préfixe matche TOUTES les variantes `["ecole","eleves", anneeId]`.
export const getEcoleDashboardQueryKey = () => ["ecole", "dashboard"] as const;
export function useGetEcoleDashboard(opts?: Partial<UseQueryOptions<api.EcoleDashboard | null>>) {
  const { anneeId } = useAnneeScope();
  return useQuery({
    queryKey: [...getEcoleDashboardQueryKey(), anneeId],
    queryFn: api.getEcoleDashboard,
    ...opts,
  });
}

export const getListEcoleMotifsQueryKey = () => ["ecole", "motifs"] as const;
/**
 * `includeInactifs` (écran de GESTION uniquement) → renvoie aussi les motifs
 * désactivés, pour pouvoir les réactiver. Les dropdowns de SAISIE n'envoient
 * RIEN → actifs uniquement. La clé inclut `includeInactifs` (caches distincts).
 */
export function useListEcoleMotifs(opts?: { includeInactifs?: boolean }) {
  const { anneeId } = useAnneeScope();
  const includeInactifs = opts?.includeInactifs ?? false;
  return useQuery({
    queryKey: [...getListEcoleMotifsQueryKey(), anneeId, includeInactifs],
    queryFn: () => api.listEcoleMotifs({ includeInactifs }),
  });
}
export function useCreateEcoleMotif() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: api.EcoleMotifCreate }) => api.createEcoleMotif(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: getListEcoleMotifsQueryKey() }),
  });
}
export function useUpdateEcoleMotif() {
  const qc = useQueryClient();
  return useMutation({
    // Le PATCH backend accepte { libelle?, montant?, frequence?, categorie?, actif? } :
    // on autorise les clés de EcoleMotif (actif/montantXof…) ET de EcoleMotifCreate
    // (montant/frequence) pour couvrir le toggle actif comme l'édition complète.
    mutationFn: ({ motifId, data }: { motifId: string; data: Partial<api.EcoleMotif> & Partial<api.EcoleMotifCreate> }) =>
      api.updateEcoleMotif(motifId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: getListEcoleMotifsQueryKey() }),
  });
}

/* --------------------------------------------------------------- *
 * Barèmes par classe + échéanciers (grille tarifaire réelle).
 * --------------------------------------------------------------- */

export function useClassesEcole() {
  const { anneeId } = useAnneeScope();
  return useQuery({
    queryKey: ["classes", anneeId],
    // `enabled` garantit anneeId non nul quand queryFn s'exécute.
    queryFn: () => api.listClasses(anneeId as string),
    enabled: !!anneeId,
  });
}

/** Niveaux de mon école (globaux + propres, triés) — la grille tarifaire saisit par niveau. */
export function useNiveauxEcole() {
  return useQuery({
    queryKey: ["ecole", "niveaux"],
    queryFn: api.listNiveauxEcole,
  });
}

/* --------------------------------------------------------------- *
 * Gestion des classes (page « Classes ») — créer, renommer,
 * supprimer si vide. Invalidation sur le préfixe ["classes"] :
 * toutes les années en cache sont resynchronisées.
 * --------------------------------------------------------------- */

export function useCreateClasseEcole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: api.EcoleClasseCreate) => api.createClasseEcole(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["classes"] }),
  });
}

export function useRenameClasseEcole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { classeId: string; libelle: string }) =>
      api.renameClasseEcole(v.classeId, v.libelle),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["classes"] });
      // Le roster affiche le libellé de classe joint — le rafraîchir aussi.
      qc.invalidateQueries({ queryKey: getListEcoleElevesQueryKey() });
    },
  });
}

export function useDeleteClasseEcole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (classeId: string) => api.deleteClasseEcole(classeId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["classes"] }),
  });
}

/** Tous les tarifs de l'école pour l'année — sert le résumé par motif. */
export function useMotifBaremesEcole() {
  const { anneeId } = useAnneeScope();
  return useQuery({
    queryKey: ["motif-baremes", "ecole", anneeId],
    queryFn: () => api.listMotifBaremes({ anneeScolaireId: anneeId as string }),
    enabled: !!anneeId,
  });
}

export const getMotifBaremesQueryKey = (motifId: string) =>
  ["motif-baremes", motifId] as const;

export function useMotifBaremes(motifId: string | null) {
  return useQuery({
    queryKey: getMotifBaremesQueryKey(motifId ?? ""),
    queryFn: () => api.listMotifBaremes({ motifId: motifId! }),
    enabled: !!motifId,
  });
}

export function useCreateMotifBareme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: api.MotifBaremeCreate) => api.createMotifBareme(data),
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: getMotifBaremesQueryKey(v.motifId) });
      qc.invalidateQueries({ queryKey: ["motif-baremes", "ecole"] });
    },
  });
}

export function useUpdateMotifBareme() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: api.MotifBaremeCreate }) =>
      api.updateMotifBareme(id, data),
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: getMotifBaremesQueryKey(v.data.motifId) });
      qc.invalidateQueries({ queryKey: ["motif-baremes", "ecole"] });
    },
  });
}

export function useDeleteMotifBareme(motifId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteMotifBareme(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: getMotifBaremesQueryKey(motifId) });
      qc.invalidateQueries({ queryKey: ["motif-baremes", "ecole"] });
    },
  });
}

/* Les échéances se chargent PAR MOTIF (une requête pour toute la grille) :
 * la saisie par niveau duplique chaque tranche sur les barèmes de toutes les
 * classes du niveau, il faut donc les voir toutes d'un coup pour les regrouper. */
export const getMotifEcheancesParMotifQueryKey = (motifId: string) =>
  ["motif-echeances", "motif", motifId] as const;

export function useMotifEcheancesParMotif(motifId: string) {
  return useQuery({
    queryKey: getMotifEcheancesParMotifQueryKey(motifId),
    queryFn: () => api.listMotifEcheances({ motifId }),
  });
}

/** Remplace l'échéancier complet d'UN barème (21/10/2026, audit SCOM-03). */
export function useRemplacerEcheancierParMotif(motifId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ baremeId, tranches }: { baremeId: string; tranches: api.TrancheEcheancier[] }) =>
      api.remplacerEcheancier(baremeId, tranches),
    onSuccess: () => qc.invalidateQueries({ queryKey: getMotifEcheancesParMotifQueryKey(motifId) }),
  });
}

export const getListEcoleElevesQueryKey = () => ["ecole", "eleves"] as const;
export function useListEcoleEleves() {
  const { anneeId } = useAnneeScope();
  return useQuery({
    queryKey: [...getListEcoleElevesQueryKey(), anneeId],
    queryFn: api.listEcoleEleves,
  });
}
export function useCreateEcoleEleve() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: api.EcoleEleveCreate }) => api.createEcoleEleve(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: getListEcoleElevesQueryKey() }),
  });
}
export function useUpdateEcoleEleve() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eleveId, data }: { eleveId: string; data: api.EcoleEleveUpdate }) =>
      api.updateEcoleEleve(eleveId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: getListEcoleElevesQueryKey() }),
  });
}
export function useGetEcoleEleveDetail(eleveId: string) {
  return useQuery({
    queryKey: ["ecole", "eleve-detail", eleveId],
    queryFn: () => api.getEcoleEleveDetail(eleveId),
    enabled: !!eleveId,
  });
}

/** Suppression d'un motif JAMAIS payé — 409 sinon (la pop-up propose la désactivation). */
export function useDeleteEcoleMotif() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (motifId: string) => api.deleteEcoleMotif(motifId),
    onSuccess: () => qc.invalidateQueries({ queryKey: getListEcoleMotifsQueryKey() }),
  });
}

export const getEleveMotifsFacultatifsQueryKey = (eleveId: string) =>
  ["ecole", "eleve-facultatifs", eleveId] as const;

/** Motifs facultatifs (cantine, transport…) et leur état pour l'élève. */
export function useEleveMotifsFacultatifs(eleveId: string) {
  return useQuery({
    queryKey: getEleveMotifsFacultatifsQueryKey(eleveId),
    queryFn: () => api.listEleveMotifsFacultatifs(eleveId),
    enabled: !!eleveId,
  });
}

/**
 * Active/désactive un motif facultatif pour l'élève. Invalide aussi la fiche
 * (les soldes changent : un facultatif activé entre dans le dû).
 */
export function useSetEleveMotifFacultatif(eleveId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { motifId: string; actif: boolean }) =>
      api.setEleveMotifFacultatif(eleveId, v.motifId, v.actif),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: getEleveMotifsFacultatifsQueryKey(eleveId) });
      qc.invalidateQueries({ queryKey: ["ecole", "eleve-detail", eleveId] });
    },
  });
}

export function useListEcolePaiements(params?: { status?: string }) {
  const { anneeId } = useAnneeScope();
  return useQuery({
    queryKey: ["ecole", "paiements", params?.status ?? "", anneeId],
    queryFn: () => api.listEcolePaiements(params),
  });
}
export function useCreateEcoleManualPaiement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: api.EcoleManualPaiementCreate }) =>
      api.createEcoleManualPaiement(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ecole", "paiements"] });
      qc.invalidateQueries({ queryKey: getEcoleDashboardQueryKey() });
    },
  });
}

// ---------- Vue unifiée des paiements (réconciliation) ----------

/**
 * Vue unifiée des paiements d'un établissement (grand-livre + entonnoir invité).
 * `enabled` se base sur la présence d'un `etablissementId` : on ne requête pas
 * tant que le scope (dashboard école / sélecteur économat) n'est pas résolu.
 */
export function useListPaiementsUnifies(
  etablissementId: string | undefined,
  opts?: { statut?: string; source?: string; limit?: number; offset?: number },
) {
  return useQuery({
    queryKey: ["paiements", "unifies", etablissementId ?? "", opts ?? {}],
    queryFn: () => api.listPaiementsUnifies(etablissementId as string, opts),
    enabled: !!etablissementId,
    placeholderData: keepPreviousData,
  });
}

// ---------- Économat ----------

export function useGetEconomatDashboard() {
  return useQuery({ queryKey: ["economat", "dashboard"], queryFn: api.getEconomatDashboard });
}
export function useListEconomatEcoles(params?: { page?: number; size?: number; q?: string }) {
  return useQuery({
    queryKey: ["economat", "ecoles", params ?? {}],
    queryFn: () => api.listEconomatEcoles(params),
    placeholderData: keepPreviousData,
  });
}
export function useGetEconomatEcoleDetail(ecoleId: string) {
  return useQuery({
    queryKey: ["economat", "ecole-detail", ecoleId],
    queryFn: () => api.getEconomatEcoleDetail(ecoleId),
    enabled: !!ecoleId,
  });
}
export function useListEconomatPaiements(params?: { status?: string; page?: number; size?: number }) {
  return useQuery({
    queryKey: ["economat", "paiements", params ?? {}],
    queryFn: () => api.listEconomatPaiements(params),
    placeholderData: keepPreviousData,
  });
}
export function useListEconomatAudit(params?: { page?: number; size?: number; q?: string; from?: string; to?: string }) {
  return useQuery({
    queryKey: ["economat", "audit", params ?? {}],
    queryFn: () => api.listEconomatAudit(params),
    placeholderData: keepPreviousData,
  });
}

// ---------- SEDEC ----------

// Clé prefix-safe : sans params → préfixe à 2 segments (invalidation match TOUTES
// les pages) ; avec params → clé à 3 segments (cache par page/filtre).
// Les surcharges gardent la clé sans params en `readonly string[]` (consommée par
// CsvImportCard.invalidateQueries) tout en typant la variante paramétrée.
export function getListSedecUsersQueryKey(): readonly ["sedec", "users"];
export function getListSedecUsersQueryKey(
  params: Record<string, unknown>,
): readonly ["sedec", "users", Record<string, unknown>];
export function getListSedecUsersQueryKey(params?: Record<string, unknown>) {
  return params ? (["sedec", "users", params] as const) : (["sedec", "users"] as const);
}
export function useListSedecUsers(params: {
  page: number; size: number; search?: string; role?: string; ecoleId?: string; active?: boolean;
}) {
  return useQuery({
    queryKey: getListSedecUsersQueryKey(params),
    queryFn: () => api.listSedecUsers(params),
    placeholderData: keepPreviousData,
  });
}
export function useCreateSedecUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ data }: { data: api.SedecUserCreate }) => api.createSedecUser(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: getListSedecUsersQueryKey() }),
  });
}
export function useUpdateSedecUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: api.SedecUserPatch }) =>
      api.updateSedecUser(userId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: getListSedecUsersQueryKey() }),
  });
}

export function useSetSedecUserStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, active }: { userId: string; active: boolean }) =>
      api.setSedecUserStatus(userId, active),
    onSuccess: () => qc.invalidateQueries({ queryKey: getListSedecUsersQueryKey() }),
  });
}

// Clé prefix-safe (cf. getListSedecUsersQueryKey) : sans params → préfixe 2
// segments (invalidation match toutes les pages) ; avec params → clé 3 segments.
export function getListSedecEcolesQueryKey(): readonly ["sedec", "ecoles"];
export function getListSedecEcolesQueryKey(
  params: Record<string, unknown>,
): readonly ["sedec", "ecoles", Record<string, unknown>];
export function getListSedecEcolesQueryKey(params?: Record<string, unknown>) {
  return params ? (["sedec", "ecoles", params] as const) : (["sedec", "ecoles"] as const);
}
export function useListSedecEcoles(params?: { page?: number; size?: number; q?: string; includeInactifs?: boolean }) {
  return useQuery({
    queryKey: params ? getListSedecEcolesQueryKey(params) : getListSedecEcolesQueryKey(),
    queryFn: () => api.listSedecEcoles(params),
    placeholderData: keepPreviousData,
  });
}
export function useListSedecEcoleOptions() {
  return useQuery({ queryKey: ["sedec", "ecoles", "options"], queryFn: api.listSedecEcoleOptions });
}
export function useUpdateSedecEcole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ecoleId, data }: { ecoleId: string; data: Partial<api.SedecEcole> }) =>
      api.updateSedecEcole(ecoleId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: getListSedecEcolesQueryKey() }),
  });
}

export function useListSedecAudit(params?: { page?: number; size?: number; q?: string; from?: string; to?: string }) {
  return useQuery({
    queryKey: ["sedec", "audit", params ?? {}],
    queryFn: () => api.listSedecAudit(params),
    placeholderData: keepPreviousData,
  });
}

/** Parents de mon école (recherche facultative sur nom/prénoms/téléphone/email). */
export function useParents(search?: string) {
  return useQuery({
    queryKey: ["ecole", "parents", search ?? ""],
    queryFn: () => api.listParents(search),
    placeholderData: keepPreviousData,
  });
}

/** Corrige une fiche parent → rafraîchit la liste (le nom part sur les reçus). */
export function useUpdateParent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: api.ParentFichePatch }) =>
      api.updateParent(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ecole", "parents"] }),
  });
}

/* --------------------------- Comptes de reversement --------------------------- */

const COMPTES_REVERSEMENT_KEY = ["ecole", "comptes-reversement"] as const;

export function useListComptesReversement(complet?: boolean) {
  return useQuery({
    queryKey: [...COMPTES_REVERSEMENT_KEY, { complet: !!complet }],
    queryFn: () => api.listComptesReversement(complet),
  });
}

export function useCreateCompteReversement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: api.CompteReversementInput) => api.createCompteReversement(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: COMPTES_REVERSEMENT_KEY }),
  });
}

export function useUpdateCompteReversement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: api.CompteReversementInput }) =>
      api.updateCompteReversement(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: COMPTES_REVERSEMENT_KEY }),
  });
}

export function useVerifierCompteReversement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.verifierCompteReversement(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: COMPTES_REVERSEMENT_KEY }),
  });
}

export function useDesactiverCompteReversement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.desactiverCompteReversement(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: COMPTES_REVERSEMENT_KEY }),
  });
}

export function useListComptesReversementReseau() {
  return useQuery({
    queryKey: ["sedec", "comptes-reversement"],
    queryFn: () => api.listComptesReversementReseau(),
  });
}

/** Suivi des écoles du diocèse courant (SEDEC). */
export function useSuiviEcoles() {
  return useQuery({
    queryKey: ["sedec", "suivi-ecoles"],
    queryFn: () => api.getSuiviEcoles(),
  });
}
